import type { RaceDirectorCue } from './game/race-director.js';

/**
 * Touch devices have a fraction of the screen and the player's thumbs are
 * already covering the corners. Notifications there follow a stricter policy
 * than on desktop: only race-defining beats interrupt, nothing stacks, and
 * routine system chatter goes to the race-control log instead of the screen.
 */

/** Director beats that change what the player must do right now. */
const ESSENTIAL_CUES = new Set([
  'launch',
  'nemesis-launch',
  'final-lap',
  'finish',
  'elimination-danger',
  'track-event-active',
  'heat-escalation',
  'boss-phase',
  'road-rage-target',
]);

/** Minimum quiet time between two director callouts on a touch screen. */
export const COMPACT_CUE_COOLDOWN_MS = 7_000;
/** How long a compact director callout stays up. */
export const COMPACT_CUE_DURATION_MS = 1_800;

export class CompactCueGate {
  private lastShownAt = Number.NEGATIVE_INFINITY;

  allow(cue: Pick<RaceDirectorCue, 'id' | 'strength'>, now: number): boolean {
    const essential = ESSENTIAL_CUES.has(cue.id);
    if (!essential) return false;
    // Finish and final lap always land; everything else respects the cooldown.
    const critical = cue.id === 'finish' || cue.id === 'final-lap';
    if (!critical && now - this.lastShownAt < COMPACT_CUE_COOLDOWN_MS) return false;
    this.lastShownAt = now;
    return true;
  }

  reset(): void {
    this.lastShownAt = Number.NEGATIVE_INFINITY;
  }
}

export type ToastTone = 'info' | 'error';

export interface ToastOptions {
  /**
   * Confirms something the player can already see happen (fullscreen on,
   * paint equipped, calibration saved). Desktop still shows it; touch keeps it
   * in the race-control log only.
   */
  quiet?: boolean;
}

export interface ToastDecision {
  /** Show on screen at all. Hidden toasts still land in the log. */
  show: boolean;
  /** Replace whatever is on screen instead of stacking. */
  replace: boolean;
  durationMs: number;
}

/**
 * Desktop keeps the stacked toasts. Touch shows a single pill at a time, keeps
 * informational chatter out of a running race entirely, drops quiet
 * confirmations and shortens the rest.
 */
export function toastDecision(tone: ToastTone, compact: boolean, racing: boolean, options: ToastOptions = {}): ToastDecision {
  if (!compact) return { show: true, replace: false, durationMs: 3_600 };
  if (tone === 'info' && (racing || options.quiet)) return { show: false, replace: true, durationMs: 0 };
  return { show: true, replace: true, durationMs: tone === 'error' ? 3_200 : 2_000 };
}

/** How long an identical message stays muted after it was shown. */
export const TOAST_REPEAT_WINDOW_MS = 8_000;

/**
 * Reconnect loops and lobby churn can fire the same sentence several times in
 * a row; on a phone that reads as nagging. Identical text is shown once per
 * window.
 */
export class ToastRepeatGate {
  private readonly shownAt = new Map<string, number>();

  allow(message: string, now: number): boolean {
    const last = this.shownAt.get(message);
    if (last !== undefined && now - last < TOAST_REPEAT_WINDOW_MS) return false;
    this.shownAt.set(message, now);
    if (this.shownAt.size > 32) {
      for (const [text, at] of this.shownAt) if (now - at >= TOAST_REPEAT_WINDOW_MS) this.shownAt.delete(text);
    }
    return true;
  }
}

/** Style callouts (drift, near miss…) on touch: only the big moments. */
export function compactStyleCalloutAllowed(action: string, multiplier: number): boolean {
  if (action === 'CHAIN LOST' || action === 'WHEELSPIN') return false;
  return multiplier >= 2;
}
